import { useEffect, useMemo, useState } from "react";

interface Props {
  /** MCP server URL (e.g. https://mcp.context7.com/mcp). Used to derive
   *  the icon source. If null/undefined, only the letter avatar renders. */
  url?: string | null;
  /** Display label — first letter is used for the placeholder avatar. */
  label: string;
  size?: number;
  className?: string;
}

/**
 * Connector icon with a graceful fallback chain:
 *
 *   1. `/manifest.json` → `icons[<largest>].src`
 *   2. `/favicon.png`
 *   3. `/favicon.ico`
 *   4. Letter avatar on a deterministic-coloured background
 *
 * The full chain is resolved once per origin in JS (Image probe) and the
 * final src is cached in module memory so subsequent mounts of this
 * component (e.g. re-entering the Connectors page) render the right icon
 * synchronously — no flicker through the fallback stages.
 */
export function ConnectorIcon({ url, label, size = 32, className }: Props) {
  const origin = useMemo(() => {
    if (!url) return null;
    try { return new URL(url).origin; } catch { return null; }
  }, [url]);

  // Hit the cache synchronously so a remount with already-resolved icon
  // renders the <img> on the first paint instead of flashing the letter.
  const initial = origin ? readCache(origin) : { state: "miss" as const };
  const [src, setSrc] = useState<string | null>(
    initial.state === "resolved" ? initial.value : null,
  );
  const resolving = initial.state === "miss" && origin !== null;

  useEffect(() => {
    if (!origin) {
      setSrc(null);
      return;
    }
    const sync = readCache(origin);
    if (sync.state === "resolved") {
      setSrc(sync.value);
      return;
    }
    let cancelled = false;
    resolveConnectorIcon(origin).then((value) => {
      if (!cancelled) setSrc(value);
    });
    return () => { cancelled = true; };
  }, [origin]);

  if (src) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        className={className}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
          background: "#ffffff",
        }}
      />
    );
  }

  // Pending or null — letter avatar. While resolving we render the letter
  // so there's never an empty hole; the swap is a single transition.
  return <LetterAvatar label={label} size={size} className={className} resolving={resolving} />;
}

function LetterAvatar({
  label,
  size,
  className,
  resolving = false,
}: {
  label: string;
  size: number;
  className?: string;
  resolving?: boolean;
}) {
  const letter = (label.trim()[0] ?? "?").toUpperCase();
  const hue = hashHue(label || "?");
  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: `hsl(${hue}, 60%, 48%)`,
        color: "white",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: Math.round(size * 0.5),
        lineHeight: 1,
        flexShrink: 0,
        opacity: resolving ? 0.85 : 1,
      }}
    >
      {letter}
    </div>
  );
}


// ── Cache + resolver ────────────────────────────────────────────────


// Final resolved icon src per origin (string) or null when no source worked.
// Module-scoped to survive SPA navigation, plus a localStorage layer to
// survive tab refresh and browser restart. The on-disk cache has a 7-day
// TTL so a service swapping its favicon eventually propagates without
// requiring a hard cache wipe.
const RESOLVED = new Map<string, string | null>();
// In-flight probes — dedupe concurrent renders for the same origin.
const PENDING = new Map<string, Promise<string | null>>();

const STORAGE_KEY = "connector-icons-v1";
const TTL_MS = 7 * 24 * 60 * 60 * 1000;
let storageHydrated = false;

interface StoredEntry {
  src: string | null;
  ts: number;
}
type StoredMap = Record<string, StoredEntry>;

function readStorage(): StoredMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as StoredMap) : {};
  } catch {
    return {};
  }
}

function writeStorageEntry(origin: string, value: string | null): void {
  if (typeof window === "undefined") return;
  try {
    const map = readStorage();
    map[origin] = { src: value, ts: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Quota exceeded / disabled storage — non-fatal.
  }
}

function hydrateFromStorage(): void {
  if (storageHydrated) return;
  storageHydrated = true;
  if (typeof window === "undefined") return;
  const map = readStorage();
  const now = Date.now();
  let dirty = false;
  for (const [origin, entry] of Object.entries(map)) {
    if (!entry || typeof entry !== "object") {
      delete map[origin];
      dirty = true;
      continue;
    }
    if (typeof entry.ts !== "number" || now - entry.ts > TTL_MS) {
      delete map[origin];
      dirty = true;
      continue;
    }
    RESOLVED.set(origin, entry.src ?? null);
  }
  if (dirty) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
  }
}

type CacheRead =
  | { state: "resolved"; value: string | null }
  | { state: "miss" };

function readCache(origin: string): CacheRead {
  hydrateFromStorage();
  if (RESOLVED.has(origin)) {
    return { state: "resolved", value: RESOLVED.get(origin) ?? null };
  }
  return { state: "miss" };
}

function resolveConnectorIcon(origin: string): Promise<string | null> {
  const inflight = PENDING.get(origin);
  if (inflight) return inflight;

  const promise = (async (): Promise<string | null> => {
    // 1. manifest.json — many origins block CORS on it; that throws and
    //    we silently fall through to the favicon paths.
    try {
      const resp = await fetch(`${origin}/manifest.json`, { mode: "cors" });
      if (resp.ok) {
        const m = await resp.json();
        const best = pickBestManifestIcon(m, origin);
        if (best && (await probeImage(best))) return best;
      }
    } catch {
      // fall through
    }
    // 2. favicon.png
    const png = `${origin}/favicon.png`;
    if (await probeImage(png)) return png;
    // 3. favicon.ico
    const ico = `${origin}/favicon.ico`;
    if (await probeImage(ico)) return ico;
    // 4. letter
    return null;
  })();

  // Persist the result and clear from pending. We keep the promise in
  // PENDING during resolution so concurrent calls share it.
  promise.then((value) => {
    RESOLVED.set(origin, value);
    writeStorageEntry(origin, value);
    PENDING.delete(origin);
  }).catch(() => {
    RESOLVED.set(origin, null);
    writeStorageEntry(origin, null);
    PENDING.delete(origin);
  });

  PENDING.set(origin, promise);
  return promise;
}

function probeImage(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
}

interface ManifestIcon {
  src?: unknown;
  sizes?: unknown;
}

function pickBestManifestIcon(manifest: unknown, origin: string): string | null {
  if (!manifest || typeof manifest !== "object") return null;
  const icons = (manifest as { icons?: unknown }).icons;
  if (!Array.isArray(icons)) return null;

  let bestSrc: string | null = null;
  let bestArea = -1;

  for (const ic of icons as ManifestIcon[]) {
    if (!ic || typeof ic !== "object" || typeof ic.src !== "string") continue;
    const sizesAttr = typeof ic.sizes === "string" ? ic.sizes : "";
    const area = sizesAttr
      .split(/\s+/)
      .map((s) => /^(\d+)x(\d+)$/.exec(s))
      .filter((m): m is RegExpExecArray => m !== null)
      .reduce((max, m) => {
        const w = parseInt(m[1] ?? "0", 10);
        const h = parseInt(m[2] ?? "0", 10);
        return Math.max(max, w * h);
      }, 0);
    // Treat a missing/unknown sizes attribute as "any" — better than 16x16
    // when the manifest only ships one icon.
    const effectiveArea = area || 1;
    if (effectiveArea > bestArea) {
      bestArea = effectiveArea;
      bestSrc = ic.src;
    }
  }

  if (!bestSrc) return null;
  try {
    return new URL(bestSrc, `${origin}/`).toString();
  } catch {
    return null;
  }
}

function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}
